// Display helpers for in-app notifications (titles, descriptions, icons, links).
// Use `$VAR` placeholders and `.replace("$VAR", value)` for data from notification.info.

"use strict";

import type { RouteLocationRaw } from "vue-router";
import { translate as t } from "@/i18n";

export type NotificationInfo = Record<string, unknown> | null | undefined;

const DEFAULT_NOTIFICATION_ROUTE: RouteLocationRaw = { name: "account-settings" };

function infoString(info: NotificationInfo, key: string): string {
    if (!info || typeof info[key] !== "string") {
        return "";
    }
    return info[key] as string;
}

/**
 * Localized title for a notification type.
 */
export function getNotificationTitle(type: string): string {
    switch (type) {
        case "welcome":
            return t("Welcome");
        case "system":
            return t("System");
        default:
            return t("Notification");
    }
}

/**
 * Localized description for a notification type.
 * Built only from structured `info` fields (never from a free-form backend message).
 */
export function getNotificationDescription(type: string, info?: NotificationInfo): string {
    switch (type) {
        case "welcome":
            return t("Welcome to the platform, $USERNAME.").replace("$USERNAME", infoString(info, "username"));
        case "system":
            switch (infoString(info, "event")) {
                case "settings_updated":
                    return t("Your account settings were updated.");
                case "maintenance":
                    return t("Maintenance is scheduled for $WHEN.").replace("$WHEN", infoString(info, "scheduledAt"));
                default:
                    return t("System notice.");
            }
        default:
            return t("You have a new notification.");
    }
}

/**
 * Font Awesome icon class for a notification type.
 */
export function getNotificationIcon(type: string): string {
    switch (type) {
        case "welcome":
            return "fa-user-check";
        case "system":
            return "fa-gear";
        default:
            return "fa-bell";
    }
}

/**
 * In-app target for a notification.
 * Optional overrides via structured info:
 * - `route`: vue-router route name
 * - `path`: absolute path starting with `/`
 */
export function getNotificationLink(type: string, info?: NotificationInfo): RouteLocationRaw {
    const routeName = infoString(info, "route");
    if (routeName) {
        return { name: routeName };
    }

    const path = infoString(info, "path");
    if (path.startsWith("/")) {
        return path;
    }

    switch (type) {
        case "system":
            switch (infoString(info, "event")) {
                case "settings_updated":
                    return { name: "account-settings" };
                default:
                    return DEFAULT_NOTIFICATION_ROUTE;
            }
        default:
            return DEFAULT_NOTIFICATION_ROUTE;
    }
}
